/**
 * Builds a tide curve from its highs and lows.
 *
 * Needed because two thirds of CO-OPS tide stations are *subordinate*: they have
 * no harmonic constituents of their own, so NOAA computes their predictions as
 * time and height offsets from a reference station and can publish only the
 * resulting high and low events. Asking such a station for `interval=30` returns
 * an error at every interval, which is why they showed as having no data.
 *
 * Between one extreme and the next, the tide is very close to half a cosine —
 * stationary at each turn, fastest halfway between. This is the standard
 * approximation, and the same shape the "rule of twelfths" crudely tabulates.
 *
 * It is an interpolation of NOAA's own numbers, not a measurement and not a
 * harmonic prediction: the turns and their heights are exactly what NOAA
 * published, while the shape between them is inferred. Callers are expected to
 * say so — see `derived` on TideDay.
 */

export interface CurvePoint {
  t: Date;
  v: number;
}

export interface Extreme {
  t: Date;
  heightFt: number;
}

/**
 * Height between two turns, following a half-cosine.
 *
 * At `a` it returns a's height, at `b` it returns b's, and it is flat at both —
 * which is the property that matters, since a tide genuinely does stand still
 * around its turn and a straight line between extremes does not.
 */
export function heightBetween(a: Extreme, b: Extreme, at: Date): number {
  const span = b.t.getTime() - a.t.getTime();
  if (span <= 0) return a.heightFt;

  const p = (at.getTime() - a.t.getTime()) / span;
  if (p <= 0) return a.heightFt;
  if (p >= 1) return b.heightFt;

  return a.heightFt + (b.heightFt - a.heightFt) * ((1 - Math.cos(Math.PI * p)) / 2);
}

/**
 * A curve at `stepMinutes` spanning the given extremes.
 *
 * Returns nothing for fewer than two extremes: one turn says a height and a
 * time, but nothing about the shape either side of it, and inventing a slope
 * from a single point would be drawing something NOAA never said.
 */
export function curveFromExtremes(
  extremes: ReadonlyArray<Extreme>,
  stepMinutes = 30,
): CurvePoint[] {
  if (extremes.length < 2) return [];

  const sorted = [...extremes].sort((x, y) => x.t.getTime() - y.t.getTime());
  const step = stepMinutes * 60_000;
  const out: CurvePoint[] = [];

  const start = sorted[0].t.getTime();
  const end = sorted[sorted.length - 1].t.getTime();

  let i = 0;
  for (let ms = start; ms <= end; ms += step) {
    // Advance to the pair bracketing this instant.
    while (i < sorted.length - 2 && ms > sorted[i + 1].t.getTime()) i++;
    const at = new Date(ms);
    out.push({ t: at, v: heightBetween(sorted[i], sorted[i + 1], at) });
  }

  // The final turn is the most interesting point on the chart; a step that does
  // not divide the span evenly would otherwise stop just short of it.
  const last = sorted[sorted.length - 1];
  if (out.length === 0 || out[out.length - 1].t.getTime() < end) {
    out.push({ t: last.t, v: last.heightFt });
  }

  return out;
}
